// Cierre de la apertura en el silo: con la cuenta OPEN o LINKED se cierra; el 409 ACCOUNT_NOT_READY reintenta.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ApiError } from '@/api/errors';
import { ACCOUNT_POLL_MS_SLOW } from '../accountWait';
import { ACCOUNT_OPENING_OPENED } from '../accountOpening';
import { useCompleteAccountOpening } from './useWizard';

export interface AccountOpeningCompletionOptions {
    /** Apertura con la cuenta ya emitida: recién ahí se puede cerrar. */
    ready: boolean;
    /** Sin domicilio no se cierra. */
    addressPending: boolean;
    /** Paso al que sigue la apertura cerrada. */
    nextStep: string;
}

export interface AccountOpeningCompletion {
    /** El cierre falló por algo distinto de la cuenta que aún no está lista. */
    failed: boolean;
    error: Error | null;
    retry: () => void;
}

function isAccountNotReady(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'ACCOUNT_NOT_READY';
}

export function useAccountOpeningCompletion(
    sessionId: string,
    { ready, addressPending, nextStep }: AccountOpeningCompletionOptions,
): AccountOpeningCompletion {
    const navigate = useNavigate();
    const { mutateAsync: completeOpening, error } = useCompleteAccountOpening(sessionId);
    const [attempt, setAttempt] = useState(0);
    const lastAttempt = useRef<number | null>(null);
    const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        const timer = retryTimer;
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        if (!ready || addressPending) return;
        if (lastAttempt.current === attempt) return;
        lastAttempt.current = attempt;
        // CANCELLED no navega: la sesión cerrada ya está en caché y el paso pinta su aviso.
        completeOpening()
            .then((closed) => {
                if (closed.state !== 'COMPLETED') return;
                toast.success(ACCOUNT_OPENING_OPENED);
                void navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: nextStep },
                });
            })
            .catch((failure: unknown) => {
                if (!isAccountNotReady(failure)) return;
                retryTimer.current = setTimeout(() => {
                    setAttempt((n) => n + 1);
                }, ACCOUNT_POLL_MS_SLOW);
            });
    }, [ready, addressPending, attempt, completeOpening, navigate, sessionId, nextStep]);

    return {
        failed: error != null && !isAccountNotReady(error),
        error,
        retry: () => {
            setAttempt((n) => n + 1);
        },
    };
}
